import { Box, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { DateRange } from "@mui/x-date-pickers-pro";
import { DateCalendar, DateField, DateRangeCalendar, SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { endOfDay, formatISO, isSameDay, isValid, parseISO } from "date-fns";
import { useState } from "react";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import type { DateFilter, DateFilterOperator, FilterValue } from "../filterTypes";
import type { DateFilterShortcut } from "../handlers/dateFilter";
import { dateFilterOperatorOptions, dateFilterShortcuts } from "../handlers/dateFilter";
import EmptyFilterBody from "./EmptyFilterBody";
import OperatorSelect from "./OperatorSelect";

interface Props {
  filter: DateFilter;
  showOperator?: boolean;
  onUpdateValue: (newValue: FilterValue) => void;
}

type ToggleValue = "exact_date" | "custom_range";

const isEmptyFilterBody = (operator: DateFilterOperator) => ["empty", "not_empty"].includes(operator);

const getOperatorOptions = (filter: DateFilter) =>
  filter.operatorOptions !== undefined
    ? dateFilterOperatorOptions.filter((option) => filter.operatorOptions?.includes(option.value))
    : dateFilterOperatorOptions;

const dateRangeToStringRange = ([dateFrom, dateTo]: DateRange<Date>): [string, string] => [
  dateFrom && isValid(dateFrom) ? formatISO(dateFrom) : "",
  dateTo && isValid(dateTo) ? formatISO(dateTo) : "",
];

const stringRangeToDateRange = (range: [string, string] | undefined): DateRange<Date> => {
  if (!range) {
    return [null, null];
  }
  const [dateFromString, dateToString] = range;
  const dateFrom = parseISO(dateFromString);
  const dateTo = parseISO(dateToString);
  return [isValid(dateFrom) ? dateFrom : null, isValid(dateTo) ? dateTo : null];
};

const DateFilterEditor = ({ filter, showOperator, onUpdateValue }: Props) => {
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>(() => stringRangeToDateRange(filter.value.range));

  const [toggleValue, setToggleValue] = useState<ToggleValue>(() => {
    const [dateFrom, dateTo] = stringRangeToDateRange(filter.value.range);
    if (!dateFrom || !dateTo || isSameDay(dateFrom, dateTo)) {
      return "exact_date";
    }
    return "custom_range";
  });

  const updateDateValuesInFilter = useDebounce((dateRange: DateRange<Date>) => {
    onUpdateValue({
      operator: filter.value.operator,
      range: dateRangeToStringRange(dateRange),
    });
  }, 500);

  const handleOperatorChange = (operator: DateFilterOperator) => {
    setSelectedRange([null, null]);
    onUpdateValue({ operator, range: ["", ""] });
  };

  const handleModeToggle = (_: React.MouseEvent, value: ToggleValue | null) => {
    if (value) {
      setToggleValue(value);
    }
  };

  const handleDateChange = (value: Date | null) => {
    setSelectedRange([value, value]);
    updateDateValuesInFilter([value, value ? endOfDay(value) : null]);
  };

  const handleDateRangeChange = (value: DateRange<Date>) => {
    setSelectedRange(value);
    updateDateValuesInFilter([value[0], value[1] ? endOfDay(value[1]) : null]);
  };

  const handleShortcutClick = (shortcut: DateFilterShortcut) => () => handleDateRangeChange(shortcut.getValue());

  const emptyFilterBody = isEmptyFilterBody(filter.value.operator);
  const operatorOptions = getOperatorOptions(filter);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {showOperator && (
        <OperatorSelect<DateFilterOperator>
          filterName={filter.name}
          options={operatorOptions}
          value={filter.value.operator}
          onChange={handleOperatorChange}
        />
      )}
      {emptyFilterBody && (
        <Box sx={(t) => ({ width: t.spacing(47.5) })}>
          <EmptyFilterBody />
        </Box>
      )}
      {!emptyFilterBody && (
        <Stack spacing={2}>
          <ToggleButtonGroup color="primary" fullWidth exclusive value={toggleValue} onChange={handleModeToggle}>
            <ToggleButton value="exact_date">Exact Date</ToggleButton>
            <ToggleButton value="custom_range">Custom Range</ToggleButton>
          </ToggleButtonGroup>
          <Divider />
          {toggleValue === "exact_date" && (
            <Stack spacing={1}>
              <DateField value={selectedRange[0]} onChange={handleDateChange} clearable />
              <DateCalendar value={selectedRange[0]} onChange={handleDateChange} />
            </Stack>
          )}
          {toggleValue === "custom_range" && (
            <Stack direction="row" spacing={2}>
              <Stack spacing={1.5}>
                {dateFilterShortcuts.map((shortcut) => (
                  <Chip key={shortcut.label} label={shortcut.label} onClick={handleShortcutClick(shortcut)} />
                ))}
              </Stack>
              <Divider flexItem orientation="vertical" />
              <Stack spacing={1}>
                <SingleInputDateRangeField value={selectedRange} onChange={handleDateRangeChange} clearable />
                <DateRangeCalendar value={selectedRange} onChange={handleDateRangeChange} calendars={1} />
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default DateFilterEditor;
